import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { useEffect, useMemo } from 'react';
import { z } from 'zod';

import {
  type CustomFieldLevel,
  type CustomFieldType,
  type CustomFieldVisibility,
} from '~/constants/customFields';
import { ENDPOINT } from '~/constants/endpoints';
import {
  PAGINATION_SORT_DIRECTION,
  PaginationSortDirection,
} from '~/constants/pagination';

import { type UUID } from '~/types/common';
import { type UncefactUnitType } from '~/types/unit';

import { vestigasApi } from '~/services/kyClient';

import { CustomFieldObject } from '~/models/customData';

import { toSnakeCase, withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import { log } from '~/utils';

import { queryKeysCustomField } from './queryKeys';
import { useQueryCustomFieldsBulk } from './useQueryCustomFieldsBulk';

export type CustomFieldsQueryParams = {
  language?: string;
  limit?: number;
  offset?: number;
  orderBy?: string;
  searchString?: string;
  sortOrder?: PaginationSortDirection;
};

export type CustomFieldsListItem = {
  allowedUnits: UncefactUnitType[];
  defaultUnit: UncefactUnitType | undefined;
  displayName: string;
  hasOptions: boolean;
  helpText: string;
  id: UUID;
  level: CustomFieldLevel;
  name: string;
  public: boolean;
  required: boolean;
  sorting: number;
  type: CustomFieldType;
  usedBy: UUID[];
  visibility: CustomFieldVisibility;
};

type CustomFieldsResponse = {
  data: readonly CustomFieldsListItem[];
  hasNextPage: boolean;
  paginatedCount: number;
  totalCount: number;
};

const zodCustomFieldsListItemSchema = z.object({
  allowedUnits: z.array(z.string()).nullish(),
  defaultUnit: z.string().nullish(),
  displayName: z.string(),
  hasOptions: z.boolean(),
  helpText: z.string().nullish(),
  id: z.uuid(),
  level: z.string(),
  name: z.string(),
  public: z.boolean(),
  required: z.boolean(),
  sorting: z.number().nullish(),
  type: z.string().nullish(),
  usedBy: z.array(z.uuid()),
  visibility: z.string().nullish(),
});

const zodCustomFieldsResponseSchema = z.object({
  data: z.array(zodCustomFieldsListItemSchema),
  hasNextPage: z.boolean(),
  paginatedCount: z.number().int().nonnegative(),
  totalCount: z.number().int().nonnegative(),
});

const zodCustomFieldsQueryParamsSchema = z
  .object({
    language: z.string().nullish(),
    limit: z.number().nullish(),
    offset: z.number().nullish(),
    orderBy: z.preprocess(
      (value) => (typeof value === 'string' ? toSnakeCase(value) : value),
      z.string().nullish(),
    ),
    searchString: z.string().nullish(),
    sortOrder: z.preprocess(
      (value) => (typeof value === 'string' ? value.toUpperCase() : value),
      z.enum(PAGINATION_SORT_DIRECTION).nullish(),
    ),
  })
  .transform((data) => withoutObjectKeysWhereValueIs(data, [null, undefined]));

/**
 * Generates the query options for the CustomFields query so they can be shared between the useQuery hook and the prefetching.
 */
export const getCustomFieldsQueryOptions = ({
  queryParams,
  options = {},
  includeOptions = false,
}: {
  queryParams: Partial<CustomFieldsQueryParams>;
  options?: Omit<UseQueryOptions<CustomFieldsResponse>, 'queryKey' | 'queryFn'>;
  includeOptions?: boolean;
}) => {
  return {
    queryFn: async () => fetchCustomFields(queryParams),
    queryKey: queryKeysCustomField.getAll(queryParams, includeOptions),
    staleTime: ms('60s'), // prevent hitting the rate limit of 30 requests/s
    ...options,
  };
};

/**
 * Fetches all CustomFields from the API.
 *
 * @param queryParams - The query parameters for fetching CustomFields.
 * @returns The custom fields data.
 * @throws Error if the API request fails.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Custom-Field/operation/get_custom_fields_paginated_custom_field_get
 */
const fetchCustomFields = async (
  queryParams: Partial<CustomFieldsQueryParams>,
) => {
  const response = await vestigasApi
    .get(ENDPOINT.CUSTOM_FIELD.SEARCH(), {
      searchParams: queryParams,
      validate: {
        searchParams: zodCustomFieldsQueryParamsSchema,
      },
    })
    .json<CustomFieldsResponse>();

  const validatedResponse = zodCustomFieldsResponseSchema.safeParse(response);
  if (!validatedResponse.success && validatedResponse.error.issues.length > 0) {
    log.error({
      message: 'Custom fields API response has validation errors',
      error: {
        errors: validatedResponse.error.issues,
        errorMessage: validatedResponse.error.message,
        response,
      },
    });
  }

  return (
    response ?? {
      data: [],
      hasNextPage: false,
      paginatedCount: 0,
      totalCount: 0,
    }
  );
};

/**
 * React Query based custom hook for getting the data for all CustomFields with given query parameters.
 * Handles pagination and prefetches the next page of CustomFields for better performance.
 *
 * @param queryParams - The query parameters for fetching CustomFields.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Custom-Field/operation/get_all_custom_fields_custom_field_all_get
 */
export const useQueryCustomFields = (
  queryParams: Partial<CustomFieldsQueryParams> = {},
  options?: Omit<
    UseQueryOptions<CustomFieldsResponse>,
    'queryKey' | 'queryFn'
  > & { includeOptions?: boolean },
) => {
  const { includeOptions = false, ...restOptions } = options ?? {};
  const queryClient = useQueryClient();

  useEffect(() => {
    void queryClient.prefetchQuery(
      getCustomFieldsQueryOptions({
        options: restOptions,
        queryParams,
        includeOptions,
      }),
    );
  }, [includeOptions, queryClient, queryParams, restOptions]);

  const baseQuery = useQuery<CustomFieldsResponse>({
    placeholderData: keepPreviousData,
    select: (response) =>
      response?.data?.map((customField) =>
        CustomFieldObject.create(customField),
      ) ?? [],
    ...getCustomFieldsQueryOptions({
      options: restOptions,
      queryParams,
      includeOptions,
    }),
  });

  // Get IDs of enumeration fields that need options
  const enumFieldIds = useMemo(
    () =>
      includeOptions && baseQuery?.data
        ? baseQuery.data
            .filter(
              (customField) =>
                customField.hasOptions &&
                customField.type === CustomFieldObject.TYPE.ENUMERATION.KEY,
            )
            .map((customField) => customField.id)
        : [],
    [baseQuery?.data, includeOptions],
  );

  // Fetch options for enumeration fields (only when needed)
  const optionsQuery = useQueryCustomFieldsBulk(enumFieldIds, {
    enabled: includeOptions && enumFieldIds.length > 0,
  });

  // Merge options into custom fields
  const data = useMemo(() => {
    if (!baseQuery.data) return undefined;
    if (!includeOptions || !optionsQuery.data) return baseQuery.data;

    const optionsMap = new Map(
      optionsQuery.data.map((customField) => [
        customField.id,
        customField.options,
      ]),
    );
    return baseQuery.data.map((customField) => ({
      ...customField,
      options: optionsMap.get(customField.id) ?? customField.options ?? [],
    }));
  }, [baseQuery.data, optionsQuery.data, includeOptions]);

  return {
    ...baseQuery,
    data,
    error: baseQuery.error ?? (includeOptions ? optionsQuery.error : undefined),
    isError: baseQuery.isError || (includeOptions && optionsQuery.isError),
    isFetching:
      baseQuery.isFetching || (includeOptions && optionsQuery.isFetching),
    isLoading:
      baseQuery.isLoading || (includeOptions && optionsQuery.isLoading),
    isSuccess:
      baseQuery.isSuccess && (!includeOptions || optionsQuery.isSuccess),
  };
};
